/**
 * Generic list panel for module-specific agent workspace tabs.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, List } from 'lucide-react';

const STATUS_TONES = {
  amber: 'bg-amber-500/15 text-amber-300 border-amber-400/25',
  green: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25',
  blue: 'bg-blue-500/15 text-blue-300 border-blue-400/25',
  red: 'bg-red-500/15 text-red-300 border-red-400/25',
  slate: 'bg-white/10 text-white/70 border-white/15',
  purple: 'bg-violet-500/15 text-violet-300 border-violet-400/25',
};

function StatusBadge({ label, tone = 'slate' }) {
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
        STATUS_TONES[tone] || STATUS_TONES.slate
      }`}
    >
      {label}
    </span>
  );
}

export default function AIAgentGenericPanel({
  title,
  items = [],
  isLoading = false,
  onTreatItem,
  emptyMessage,
  showWhenEmpty = false,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.genericPanel.${key}`, opts);
  const panelTitle = title || p('default_title');
  const panelEmpty = emptyMessage || p('empty');
  if (isLoading) {
    return (
      <section className="mx-auto w-full max-w-4xl mb-4">
        <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.08] via-blue-950/20 to-indigo-950/30 backdrop-blur-lg overflow-hidden animate-pulse">
          <div className="px-5 sm:px-6 py-4 border-b border-white/10">
            <div className="h-5 w-40 bg-white/10 rounded-lg" />
          </div>
          <div className="px-5 sm:px-6 py-8 space-y-3">
            <div className="h-10 bg-white/5 rounded-2xl" />
            <div className="h-10 bg-white/5 rounded-2xl" />
          </div>
        </div>
      </section>
    );
  }

  if (!items.length && !showWhenEmpty) return null;

  return (
    <section className="mx-auto w-full max-w-4xl mb-4">
      <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.08] via-blue-950/20 to-indigo-950/30 backdrop-blur-lg overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <List className="h-5 w-5 text-white/70 shrink-0" />
          <h2 className="m-0 text-base sm:text-lg font-semibold text-white">{panelTitle}</h2>
        </div>

        {items.length === 0 ? (
          <p className="m-0 px-5 sm:px-6 py-8 text-sm text-white/45 text-center">{panelEmpty}</p>
        ) : (
          <div className="divide-y divide-white/[0.06]">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-5 sm:px-6 py-4 hover:bg-white/[0.03] transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-sm sm:text-[0.95rem] font-semibold text-white">{item.title}</p>
                  {item.subtitle ? (
                    <p className="m-0 mt-1 text-xs sm:text-sm text-white/45">{item.subtitle}</p>
                  ) : null}
                  {item.statusLabel && item.statusLabel !== '—' ? (
                    <div className="mt-2">
                      <StatusBadge label={item.statusLabel} tone={item.statusTone} />
                    </div>
                  ) : null}
                </div>
                <div className="shrink-0">
                  <button
                    type="button"
                    onClick={() => onTreatItem?.(item)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-blue-500 hover:bg-blue-400 text-white transition-colors"
                  >
                    {p('treat')}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
